import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import {
  usePersonalCategories,
  type PersonalCategory,
  type CategoryType,
  type CreateCategoryInput,
  type UpdateCategoryInput,
} from '@/hooks';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Lock,
  ChevronDown,
  ChevronRight,
  Loader2,
} from 'lucide-react';

type DialogMode = 'create' | 'edit' | null;

interface CategoryFormState {
  name: string;
  icon: string;
  color: string;
  category_type: CategoryType;
  parent_id: string;
}

const EMPTY_FORM: CategoryFormState = {
  name: '',
  icon: '',
  color: '#6B7280',
  category_type: 'expense',
  parent_id: '',
};

export function PersonalCategoriesPage() {
  const {
    categories,
    incomeCategories,
    expenseCategories,
    transferCategories,
    isLoading,
    createCategory,
    updateCategory,
    deleteCategory,
  } = usePersonalCategories({ includeInactive: true });

  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [editingCategory, setEditingCategory] = useState<PersonalCategory | null>(null);
  const [form, setForm] = useState<CategoryFormState>(EMPTY_FORM);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Collect top-level categories for the parent dropdown
  const topLevelCategories = categories.filter((c) => c.parent_id === null);

  function openCreateDialog() {
    setForm(EMPTY_FORM);
    setEditingCategory(null);
    setDialogMode('create');
  }

  function openEditDialog(category: PersonalCategory) {
    setForm({
      name: category.name,
      icon: category.icon || '',
      color: category.color || '#6B7280',
      category_type: category.category_type,
      parent_id: category.parent_id || '',
    });
    setEditingCategory(category);
    setDialogMode('edit');
  }

  function closeDialog() {
    setDialogMode(null);
    setEditingCategory(null);
  }

  function toggleExpand(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast({ variant: 'destructive', title: 'Error', description: 'Name is required.' });
      return;
    }

    try {
      if (dialogMode === 'create') {
        const input: CreateCategoryInput = {
          name: form.name.trim(),
          category_type: form.category_type,
          icon: form.icon || undefined,
          color: form.color || undefined,
          parent_id: form.parent_id || undefined,
        };
        await createCategory.mutateAsync(input);
        toast({ title: 'Category created', description: `"${form.name.trim()}" has been added.` });
      } else if (dialogMode === 'edit' && editingCategory) {
        const input: UpdateCategoryInput & { id: string } = {
          id: editingCategory.id,
          name: form.name.trim(),
          icon: form.icon || undefined,
          color: form.color || undefined,
          parent_id: form.parent_id || null,
        };
        await updateCategory.mutateAsync(input);
        toast({ title: 'Category updated', description: `"${form.name.trim()}" has been saved.` });
      }
      closeDialog();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      toast({ variant: 'destructive', title: 'Error', description: message });
    }
  }

  async function handleToggleActive(category: PersonalCategory) {
    try {
      await updateCategory.mutateAsync({
        id: category.id,
        is_active: !category.is_active,
      });
      toast({
        title: category.is_active ? 'Category hidden' : 'Category restored',
        description: `"${category.name}" has been ${category.is_active ? 'deactivated' : 'reactivated'}.`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      toast({ variant: 'destructive', title: 'Error', description: message });
    }
  }

  async function handleDelete(category: PersonalCategory) {
    try {
      await deleteCategory.mutateAsync(category.id);
      toast({ title: 'Category deleted', description: `"${category.name}" has been removed.` });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      toast({ variant: 'destructive', title: 'Error', description: message });
    }
  }

  const isSaving = createCategory.isPending || updateCategory.isPending;

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      <main className="flex-1 py-8">
        <div className="container max-w-3xl">
          {/* Back link */}
          <Link
            to="/app/personal/dashboard"
            className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Personal Dashboard
          </Link>

          {/* Page header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl font-bold">Personal Categories</h1>
              <p className="mt-1 text-muted-foreground">
                Manage your personal expense and income categories
              </p>
            </div>
            <Button onClick={openCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              Add Category
            </Button>
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className="mt-12 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Category sections */}
          {!isLoading && (
            <div className="mt-8 space-y-8">
              <CategorySection
                title="Income Categories"
                categories={incomeCategories}
                expandedIds={expandedIds}
                onToggleExpand={toggleExpand}
                onEdit={openEditDialog}
                onToggleActive={handleToggleActive}
                onDelete={handleDelete}
              />
              <CategorySection
                title="Expense Categories"
                categories={expenseCategories}
                expandedIds={expandedIds}
                onToggleExpand={toggleExpand}
                onEdit={openEditDialog}
                onToggleActive={handleToggleActive}
                onDelete={handleDelete}
              />
              {transferCategories.length > 0 && (
                <CategorySection
                  title="Transfer Categories"
                  categories={transferCategories}
                  expandedIds={expandedIds}
                  onToggleExpand={toggleExpand}
                  onEdit={openEditDialog}
                  onToggleActive={handleToggleActive}
                  onDelete={handleDelete}
                />
              )}
            </div>
          )}
        </div>
      </main>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogMode !== null} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'create' ? 'Add Category' : 'Edit Category'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="cat-name">Name</Label>
              <Input
                id="cat-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Subscriptions"
                disabled={editingCategory?.is_system}
              />
            </div>

            {/* Category Type (create only) */}
            {dialogMode === 'create' && (
              <div className="space-y-2">
                <Label>Type</Label>
                <div className="flex gap-3">
                  {(['income', 'expense', 'transfer'] as const).map((type) => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="category_type"
                        value={type}
                        checked={form.category_type === type}
                        onChange={() => setForm((f) => ({ ...f, category_type: type, parent_id: '' }))}
                        className="accent-sapling-primary-100"
                      />
                      <span className="text-sm capitalize">{type}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Icon */}
            <div className="space-y-2">
              <Label htmlFor="cat-icon">Icon (Lucide name)</Label>
              <Input
                id="cat-icon"
                value={form.icon}
                onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
                placeholder="e.g. shopping-cart"
                disabled={editingCategory?.is_system}
              />
              <p className="text-xs text-muted-foreground">
                Use a Lucide icon name like "home", "car", "utensils"
              </p>
            </div>

            {/* Color */}
            <div className="space-y-2">
              <Label htmlFor="cat-color">Color</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  className="h-9 w-9 cursor-pointer rounded border p-0.5"
                  disabled={editingCategory?.is_system}
                />
                <Input
                  id="cat-color"
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  placeholder="#6B7280"
                  className="flex-1"
                  disabled={editingCategory?.is_system}
                />
              </div>
            </div>

            {/* Parent Category (expense only, not for system categories) */}
            {form.category_type === 'expense' && !editingCategory?.is_system && (
              <div className="space-y-2">
                <Label htmlFor="cat-parent">Parent Category (optional)</Label>
                <select
                  id="cat-parent"
                  value={form.parent_id}
                  onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value }))}
                  className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">None (top-level)</option>
                  {topLevelCategories
                    .filter((c) => c.category_type === 'expense' && c.id !== editingCategory?.id)
                    .map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
              </div>
            )}

            {/* System category notice */}
            {editingCategory?.is_system && (
              <div className="rounded-lg border border-dashed bg-muted/50 p-3 text-sm text-muted-foreground">
                <Lock className="mr-1.5 inline-block h-3.5 w-3.5" />
                System categories can only be shown or hidden. Name, icon, and color cannot be changed.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            {editingCategory?.is_system ? (
              <Button
                onClick={() => { handleToggleActive(editingCategory); closeDialog(); }}
              >
                {editingCategory.is_active ? 'Hide Category' : 'Show Category'}
              </Button>
            ) : (
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {dialogMode === 'create' ? 'Create' : 'Save Changes'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CategorySection — group of categories under a heading
// ---------------------------------------------------------------------------

interface CategorySectionProps {
  title: string;
  categories: (PersonalCategory & { children?: PersonalCategory[] })[];
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  onEdit: (cat: PersonalCategory) => void;
  onToggleActive: (cat: PersonalCategory) => void;
  onDelete: (cat: PersonalCategory) => void;
}

function CategorySection({
  title,
  categories,
  expandedIds,
  onToggleExpand,
  onEdit,
  onToggleActive,
  onDelete,
}: CategorySectionProps) {
  if (categories.length === 0) return null;

  return (
    <div className="rounded-card border bg-card p-6">
      <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h2>
      <div className="mt-4 divide-y">
        {categories.map((cat) => (
          <div key={cat.id}>
            <CategoryRow
              category={cat}
              hasChildren={!!cat.children && cat.children.length > 0}
              isExpanded={expandedIds.has(cat.id)}
              onToggleExpand={() => onToggleExpand(cat.id)}
              onEdit={() => onEdit(cat)}
              onToggleActive={() => onToggleActive(cat)}
              onDelete={() => onDelete(cat)}
            />
            {/* Children */}
            {cat.children && expandedIds.has(cat.id) && (
              <div className="pl-8">
                {cat.children.map((child) => (
                  <CategoryRow
                    key={child.id}
                    category={child}
                    hasChildren={false}
                    isExpanded={false}
                    onToggleExpand={() => {}}
                    onEdit={() => onEdit(child)}
                    onToggleActive={() => onToggleActive(child)}
                    onDelete={() => onDelete(child)}
                    isChild
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CategoryRow — single category item
// ---------------------------------------------------------------------------

interface CategoryRowProps {
  category: PersonalCategory;
  hasChildren: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
  isChild?: boolean;
}

function CategoryRow({
  category,
  hasChildren,
  isExpanded,
  onToggleExpand,
  onEdit,
  onToggleActive,
  onDelete,
  isChild,
}: CategoryRowProps) {
  const inactive = !category.is_active;

  return (
    <div
      className={`flex items-center gap-3 py-2.5 ${inactive ? 'opacity-50' : ''} ${isChild ? 'border-t border-dashed' : ''}`}
    >
      {/* Expand/collapse toggle or spacer */}
      {!isChild && (
        <button
          onClick={hasChildren ? onToggleExpand : undefined}
          className={`flex h-5 w-5 shrink-0 items-center justify-center ${hasChildren ? 'cursor-pointer text-muted-foreground hover:text-foreground' : ''}`}
          tabIndex={hasChildren ? 0 : -1}
        >
          {hasChildren && (
            isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
          )}
        </button>
      )}

      {/* Color dot */}
      <span
        className="h-3 w-3 shrink-0 rounded-full"
        style={{ backgroundColor: category.color || '#6B7280' }}
      />

      {/* Name */}
      <span className={`flex-1 text-sm ${inactive ? 'line-through' : ''}`}>
        {category.name}
      </span>

      {/* System badge */}
      {category.is_system && (
        <span title="System category">
          <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </span>
      )}

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={onEdit}
          title="Edit"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={onToggleActive}
          title={category.is_active ? 'Hide' : 'Show'}
        >
          {category.is_active ? (
            <EyeOff className="h-3.5 w-3.5" />
          ) : (
            <Eye className="h-3.5 w-3.5" />
          )}
        </Button>
        {!category.is_system && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
            onClick={onDelete}
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
